import type { Tool } from "ai"
import type { AssistantModule, ReviewMode, ToolPolicy } from "./types"
import { redactSecrets } from "./redact"

export interface ActionRecord {
  module: string
  tool: string
  args: unknown
  status: "done" | "rejected" | "failed"
}

export interface PolicyContext {
  can: (resource: string, action: PermissionAction) => boolean
  review: ReviewMode
  // Resolves true when approved. Already true when auto-accept is on
  ask: (summary: string, detail: string) => Promise<boolean>
  record?: (action: ActionRecord) => void
}

const WRITE: ToolPolicy = { kind: "write" }

export function policyFor(module: AssistantModule, name: string): ToolPolicy {
  return module.policies?.[name] ?? WRITE
}

export function isAllowed(module: AssistantModule, name: string, ctx: Pick<PolicyContext, "can">): boolean {
  const p = policyFor(module, name)
  if (p.kind !== "read" && module.writable === false) return false
  if (p.permission && !ctx.can(p.permission[0], p.permission[1])) return false
  return true
}

function truncate(s: string, max = 4000) {
  return s.length > max ? s.slice(0, max) + "\n…" : s
}

// The tools one step can use. A later module can't silently replace an earlier module's tool
export function buildToolSet(modules: AssistantModule[], ctx: PolicyContext): {
  tools: Record<string, Tool>
  labels: Record<string, string>
  owner: Record<string, string>
} {
  const tools: Record<string, Tool> = {}
  const labels: Record<string, string> = {}
  const owner: Record<string, string> = {}

  for (const module of modules) {
    for (const [name, t] of Object.entries(module.tools)) {
      if (name in tools) {
        console.warn(`[assistant] ${module.id} tried to register ${name}, already provided by ${owner[name]}`)
        continue
      }
      if (!isAllowed(module, name, ctx)) continue
      const policy = policyFor(module, name)
      owner[name] = module.id
      if (module.labels?.[name]) labels[name] = module.labels[name]

      const execute = (t as any).execute as ((args: any, opts: any) => PromiseLike<any>) | undefined
      if (!execute) { tools[name] = t; continue }
      // A thrown error would end the whole run, so reads return it to the model too
      if (policy.kind === "read") {
        tools[name] = {
          ...t,
          execute: async (args: any, opts: any) => {
            try { return await execute(args, opts) } catch (e: any) { return { error: e?.message || String(e) } }
          },
        } as Tool
        continue
      }

      // Every change asks in "all" mode; deletes ask in every mode. Auto-accept answers for the user
      const reviewFirst = !policy.selfReviewed && (ctx.review === "all" || policy.kind === "destructive")
      tools[name] = {
        ...t,
        execute: async (args: any, opts: any) => {
          // Don't ask anyone to approve a call that would be refused anyway
          const invalid = await policy.precheck?.(args)
          if (invalid) return { error: invalid }
          if (reviewFirst) {
            const ok = await ctx.ask(`${module.title}: ${labels[name] ?? name}`, truncate(JSON.stringify(redactSecrets(args ?? {}), null, 2)))
            if (!ok) {
              ctx.record?.({ module: module.id, tool: name, args, status: "rejected" })
              return { error: "Rejected by reviewer." }
            }
          }
          try {
            const result = await execute(args, opts)
            const failed = result && typeof result === "object" && "error" in result
            const rejected = failed && /reject/i.test(String((result as any).error ?? (result as any).status ?? ""))
            ctx.record?.({ module: module.id, tool: name, args, status: rejected ? "rejected" : failed ? "failed" : "done" })
            return result
          } catch (e: any) {
            ctx.record?.({ module: module.id, tool: name, args, status: "failed" })
            return { error: e?.message || String(e) }
          }
        },
      } as Tool
    }
  }
  return { tools, labels, owner }
}
